const RSA_PLAINTEXT_LIMIT_BYTES = 190;
let cachedCryptoKey = null;

const pemToArrayBuffer = (pem) => {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s/g, '');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const getLayoutEnv = () => {
  if (typeof window !== 'undefined' && window.ENV) return window.ENV;
  if (typeof ENV !== 'undefined') return ENV;
  return null;
};

const getDashboardTransportPublicKeyPem = () => {
  const layoutEnv = getLayoutEnv();
  if (!layoutEnv) return null;

  const pem = layoutEnv.MIA_RETAILER_AUTH_RSA_PUBLIC_KEY;
  if (!pem || typeof pem !== 'string') return null;

  const normalized = pem.trim().replace(/\\n/g, '\n');
  return normalized.length > 0 ? normalized : null;
};

const importTransportPublicKey = (publicKeyPem) => crypto.subtle.importKey(
  'spki',
  pemToArrayBuffer(publicKeyPem),
  { name: 'RSA-OAEP', hash: 'SHA-256' },
  false,
  ['encrypt']
);

const loadTransportPublicKey = async () => {
  const publicKeyPem = getDashboardTransportPublicKeyPem();
  if (!publicKeyPem) {
    throw new Error('MIA_RETAILER_AUTH_RSA_PUBLIC_KEY is not configured (dashboard ENV)');
  }

  return importTransportPublicKey(publicKeyPem);
};

const getTransportCryptoKey = async () => {
  if (!cachedCryptoKey) {
    cachedCryptoKey = await loadTransportPublicKey();
  }
  return cachedCryptoKey;
};

export const encryptRetailerAuthMethodDataForTransport = async (dataObject) => {
  const cryptoKey = await getTransportCryptoKey();
  const encoded = new TextEncoder().encode(JSON.stringify(dataObject));

  if (encoded.byteLength <= RSA_PLAINTEXT_LIMIT_BYTES) {
    const encrypted = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, cryptoKey, encoded);
    return arrayBufferToBase64(encrypted);
  }

  const aesKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encoded);
  const rawAesKey = await crypto.subtle.exportKey('raw', aesKey);
  const wrappedKey = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, cryptoKey, rawAesKey);

  return JSON.stringify({
    v: 1,
    key: arrayBufferToBase64(wrappedKey),
    iv: arrayBufferToBase64(iv.buffer),
    ciphertext: arrayBufferToBase64(ciphertext)
  });
};

export default encryptRetailerAuthMethodDataForTransport;
